import './base.css';

import React, { FC, memo } from 'react';
import enUS from 'antd/lib/locale/en_US';
import zhCN from 'antd/lib/locale/zh_CN';
import { useSelector, shallowEqual } from 'react-redux';
import { AllState } from 'src/state';
import { ConfigProvider, App } from 'antd';
import { Route, Switch } from 'react-router-dom';
import NotFound from 'src/components/404';
import Welcome from '@/components/Welcome';
import Layout from './Layout';
import { Async } from 'reaux';
import ErrorBoundary from '@/components/ErrorBoundary';
import { LOGIN_PAGE } from '@/utils/constant';
import { authBlackList } from '..';

const Login = Async(() => import(/* webpackChunkName: "login" */ '../../login'), 'View');
const OrderCity = Async(() => import(/* webpackChunkName: "order-city" */ '../../order/city'), 'View');
const OrderFtl = Async(() => import(/* webpackChunkName: "order-ftl" */ '../../order/ftl'), 'View');
const OrderOms = Async(() => import(/* webpackChunkName: "order-oms" */ '../../order/oms'), 'View');
const OrderGps = Async(() => import(/* webpackChunkName: "order-gps" */ '../../order/gps'), 'View');
const WaybillList = Async(() => import(/* webpackChunkName: "waybill-list" */ '../../waybill/list'), 'View');
const WaybillFtl = Async(() => import(/* webpackChunkName: "waybill-ftl" */ '../../waybill/ftl'), 'View');
const WaybillGps = Async(() => import(/* webpackChunkName: "waybill-gps" */ '../../waybill/gps'), 'View');
const WaybillException = Async(
  () => import(/* webpackChunkName: "waybill-exception" */ '../../waybill/exception'),
  'View',
);
const QuoteList = Async(() => import(/* webpackChunkName: "quote-list" */ '../../quote/list'), 'View');
const CustomerList = Async(() => import(/* webpackChunkName: "customer-list" */ '../../customer/list'), 'View');
const CustomerQuoteConfig = Async(
  () => import(/* webpackChunkName: "customer-quote-config" */ '../../customer/quote-config'),
  'View',
);
const CustomerPlatformQuoteConfig = Async(
  () => import(/* webpackChunkName: "customer-platform-quote-config" */ '../../customer/platform-quote-config'),
  'View',
);
const CustomerPlatformQuoteConfigStrategy = Async(
  () =>
    import(
      /* webpackChunkName: "customer-platform-quote-config-strategy" */ '../../customer/platform-quote-config-strategy'
    ),
  'View',
);
const SystemUser = Async(() => import(/* webpackChunkName: "system-user" */ '../../system/user'), 'View');
const SystemRole = Async(() => import(/* webpackChunkName: "system-role" */ '../../system/role'), 'View');
const QuoteMotherShip = Async(
  () => import(/* webpackChunkName: "quote-mothership" */ '../../quote/mothership'),
  'View',
);
const QuoteValueAddedFees = Async(
  () => import(/* webpackChunkName: "quote-value-added-fees" */ '../../quote/value-added-fees'),
  'View',
);
const WaybillPriceFees = Async(
  () => import(/* webpackChunkName: "waybill-price-fees" */ '../../quote/waybill-price-fees'),
  'View',
);
const OrderProxy = Async(() => import(/* webpackChunkName: "order-proxy" */ '../../order-proxy'), 'View');
const OrderBOL = Async(() => import(/* webpackChunkName: "order-bol" */ '../../order/bol'), 'View');
const OrderPOD = Async(() => import(/* webpackChunkName: "order-pod" */ '../../order/pod'), 'View');
const OrderFtlBol = Async(() => import(/* webpackChunkName: "order-ftl-bol" */ '../../order/ftlBol'), 'View');
const OrderFtlPod = Async(() => import(/* webpackChunkName: "order-ftl-pod" */ '../../order/ftlPod'), 'View');
const OrderProxyFtl = Async(() => import(/* webpackChunkName: "order-proxy-ftl" */ '../../order-proxy-ftl'), 'View');
const TransferCenter = Async(
  () => import(/* webpackChunkName: "transfer-center" */ '../../quote/transfer-center'),
  'View',
);

const FundRecharge = Async(() => import(/* webpackChunkName: "fund-recharge" */ '../../fund/recharge'), 'View');
const FundWithdraw = Async(() => import(/* webpackChunkName: "fund-withdraw" */ '../../fund/withdraw'), 'View');
const FundWallet = Async(() => import(/* webpackChunkName: "fund-wallet" */ '../../fund/wallet'), 'View');
const FundTrade = Async(() => import(/* webpackChunkName: "fund-trade" */ '../../fund/trade'), 'View');
const SalesChannels = Async(() => import(/* webpackChunkName: "sales-channels" */ '../../sales/channels'), 'View');
const SalesChannelCommission = Async(
  () => import(/* webpackChunkName: "sales-channel-commission" */ '../../sales/channel-commission'),
  'View',
);
const SalesChannelsCustomer = Async(
  () => import(/* webpackChunkName: "sales-channels-customer" */ '../../sales/channel-customer'),
  'View',
);
const CarrierCompany = Async(() => import(/* webpackChunkName: "carrier-company" */ '../../carrier/company'), 'View');
const AppBol = Async(() => import(/* webpackChunkName: "app-bol" */ '../../app/bol'), 'View');
const AppBolFtl = Async(() => import(/* webpackChunkName: "app-bol-ftl" */ '../../app/bol-ftl'), 'View');
const OrderDeliveryNote = Async(
  () => import(/* webpackChunkName: "order-delivery-note" */ '../../order/deliveryNote'),
  'View',
);
const OrderFtlDeliveryNote = Async(
  () => import(/* webpackChunkName: "order-ftl-delivery-note" */ '../../order/ftlDeliveryNote'),
  'View',
);
const WaybillDriverLog = Async(
  () => import(/* webpackChunkName: "waybill-driver-log" */ '../../waybill/driver-log'),
  'View',
);

const CarrierCensus = Async(() => import(/* webpackChunkName: "carrier-census" */ '../../carrier/census'), 'View');
const CarrierDriverVehicle = Async(
  () => import(/* webpackChunkName: "carrier-driver-vehicle" */ '../../carrier/driver-vehicle'),
  'View',
);

const routes = {
  '/': () => <Welcome />,
  '/order/city': () => <OrderCity />,
  '/order/ftl': () => <OrderFtl />,
  '/order/oms': () => <OrderOms />,
  '/waybill/list': () => <WaybillList />,
  '/waybill/ftl': () => <WaybillFtl />,
  '/waybill/exception': () => <WaybillException />,
  '/quote/list': () => <QuoteList />,
  '/quote/mothership': () => <QuoteMotherShip />,
  '/quote/value-added-fees': () => <QuoteValueAddedFees />,
  '/quote/waybill-price-fees': () => <WaybillPriceFees />,
  '/network/transfer-center': () => <TransferCenter />,
  '/customer/list': () => <CustomerList />,
  '/system/user': () => <SystemUser />,
  '/system/role': () => <SystemRole />,
  '/fund/recharge': () => <FundRecharge />,
  '/fund/cashout': () => <FundWithdraw />,
  '/fund/wallet': () => <FundWallet />,
  '/fund/trade': () => <FundTrade />,
  '/sales/list': () => <SalesChannels />,
  '/carrier-company/list': () => <CarrierCompany />,
  '/app/bol': () => <AppBol />,
  '/carrier-company/fmcsa': () => <CarrierCensus />,
};

const Index: FC = () => {
  const { lang, pagePaths } = useSelector(
    (state: AllState) => ({ lang: state.main.lang, pagePaths: state.main.pagePaths }),
    shallowEqual,
  );

  return (
    <ErrorBoundary>
      <ConfigProvider
        locale={lang === 'zh' ? zhCN : enUS}
        theme={{
          token: {
            fontFamily: `'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
            colorPrimary: '#FB7703',
            colorInfo: '#FB7703',
          },
          components: {
            Typography: {
              colorLink: '#1677ff',
              colorLinkActive: '#0958d9',
              colorLinkHover: '#69b1ff',
            },
            Layout: {
              headerHeight: 56,
              headerPadding: '0 18px',
              bodyBg: '#F6F6F7',
              siderBg: 'rgb(255,255,255)',
            },
            Menu: {
              subMenuItemBg: '#fff',
              itemMarginInline: 0,
              itemBorderRadius: 0,
              itemColor: '#1F1F1F',
            },
            Tabs: {
              controlHeightLG: 32,
              borderRadiusLG: 0,
              colorBorderSecondary: '#DCDFE6',
              cardGutter: 0,
              itemColor: '#303133',
            },
            Space: {
              paddingXS: 16,
            },
            Button: {
              boxShadow: 'none',
            },
            Table: {
              padding: 8,
              cellPaddingBlock: 12,
            },
          },
        }}
      >
        <App>
          <Layout blacklist={authBlackList}>
            <Switch>
              <Route exact path='/' render={() => <Welcome />} />
              <Route exact path={LOGIN_PAGE} render={() => <Login />} />
              {pagePaths?.map((path, index) => <Route key={index} exact path={path} render={routes[path]} />)}
              {pagePaths?.includes('/customer/list') && (
                <Route path={'/customer/list/order-proxy/:id'} component={() => <OrderProxy />} />
              )}
              {pagePaths?.includes('/customer/list') && (
                <Route path={'/customer/list/order-proxy-ftl/:id'} component={() => <OrderProxyFtl />} />
              )}
              {pagePaths?.includes('/customer/list') && (
                <Route path={'/customer/list/quote-config/:id'} component={() => <CustomerQuoteConfig />} />
              )}
              {pagePaths?.includes('/customer/list') && (
                <Route
                  path={'/customer/list/platform-quote-config/:id'}
                  component={() => <CustomerPlatformQuoteConfig />}
                />
              )}
              {pagePaths?.includes('/customer/list') && (
                <Route
                  path={'/customer/list/platform-quote-config-strategy/:id'}
                  component={() => <CustomerPlatformQuoteConfigStrategy />}
                />
              )}
              {pagePaths?.includes('/order/city') && (
                <Route path={'/order/city/bol/:id'} component={() => <OrderBOL />} />
              )}
              {pagePaths?.includes('/order/ftl') && (
                <Route path={'/order/ftl/bol/:id'} component={() => <OrderFtlBol />} />
              )}
              {pagePaths?.includes('/order/city') && (
                <Route path={'/order/city/delivery-note/:id'} component={() => <OrderDeliveryNote />} />
              )}
              {pagePaths?.includes('/order/ftl') && (
                <Route path={'/order/ftl/delivery-note/:id'} component={() => <OrderFtlDeliveryNote />} />
              )}
              {pagePaths?.includes('/order/city') && (
                <Route path={'/order/city/gps/:id'} component={() => <OrderGps />} />
              )}
              {pagePaths?.includes('/order/ftl') && (
                <Route path={'/order/ftl/gps/:id'} component={() => <OrderGps />} />
              )}
              {pagePaths?.includes('/order/city') && (
                <Route path={'/order/city/pod/:id'} component={() => <OrderPOD />} />
              )}
              {pagePaths?.includes('/order/ftl') && (
                <Route path={'/order/ftl/pod/:id'} component={() => <OrderFtlPod />} />
              )}
              {pagePaths?.includes('/waybill/list') && (
                <Route path={'/waybill/list/gps/:id'} component={() => <WaybillGps />} />
              )}
              {pagePaths?.includes('/waybill/ftl') && (
                <Route path={'/waybill/ftl/gps/:id'} component={() => <WaybillGps />} />
              )}
              {pagePaths?.includes('/waybill/list') && (
                <Route path={'/waybill/list/driver-log/:id'} component={() => <WaybillDriverLog />} />
              )}
              {pagePaths?.includes('/sales/list') && (
                <Route path={'/sales/list/commission/:id'} component={() => <SalesChannelCommission />} />
              )}
              {pagePaths?.includes('/sales/list') && (
                <Route path={'/sales/list/customer/:id'} component={() => <SalesChannelsCustomer />} />
              )}
              {pagePaths?.includes('/carrier-company/list') && (
                <Route path={'/carrier-company/list/driver-vehicle/:id'} component={() => <CarrierDriverVehicle />} />
              )}
              {/* 用于 app 展示 bol */}
              <Route exact path={'/app/bol-ftl'} component={() => <AppBolFtl />} />
              <Route exact path={'/app/bol'} component={() => <AppBol />} />
              {pagePaths !== null && <Route path='*' component={() => <NotFound />} />}
            </Switch>
          </Layout>
        </App>
      </ConfigProvider>
    </ErrorBoundary>
  );
};

export default memo(Index);
